import { z } from "zod/v4";

import { BillingContextSchema } from "../routing";
import { ShopifyProviderApprovalList } from "../../stripe-subscription-links";
import {
  type StripeInterval,
  StripeIntervalList,
  SubscriptionBillingProviderList,
  SubscriptionIntervalList,
} from "../../subscriptions";

import {
  BillingCadence,
  BillingCadenceList,
  BillingEffectiveTimingList,
  BillingScheduledBoundaryReasonList,
  BillingScheduledFollowupCommandTypeList,
} from "./catalog";
import { BillingNormalizedIntentV1Schema } from "./intents";
import { BillingPlanIntentSnapshotV1Schema } from "./plan-intent";
import {
  BillingPlanIntentRefV1Schema,
  ShopifyMoneyV1Schema,
} from "./plan-intent-ref";
import {
  AgencyLifecyclePayloadSchema,
  BillingFollowupPayloadSchema,
  BillingReleaseScheduledWakeupV1Schema,
  BillingResolvedProductSetTargetSchema,
  BillingScheduledWakeupV1Schema,
  ProviderLifecycleActionSchema,
} from "./payloads";
import {
  ExistingSubscriptionRefsSchema,
  IdempotencyKeysSchema,
  ShopifyExecutionModeSchema,
  ShopifyExistingRefsSchema,
  StripeProviderExecutionInputsSchema,
  StripeProviderMutationV1Schema,
} from "./provider-execution";
import { BillingCommandStatusList } from "./status";
import {
  BillingCommandRuntimeEnvelopeV1Schema,
  BillingRuntimeCheckpointV1Schema,
} from "./runtime";
import { billingStateAssertionError } from "./state-assertion-error";

const BillingCatalogProductV1Schema = z.object({
  subscriptionId: z.string().min(1),
  name: z.string().min(1),
  // The entitlement profile key drives product classification (bundle
  // eligibility, Smart Pricing / Unlimited detection). It must survive
  // persistence so the policy path classifies by key, not by product name.
  // Nullable with a null default keeps older checkpoints (written before this
  // field existed) parseable — they fall back to name-based classification.
  entitlementProfileKey: z.string().nullable().default(null),
  stripeProductId: z.string().nullish(),
  stripePriceId: z.string().nullish(),
  defaultInterval: z.enum(SubscriptionIntervalList),
  defaultPrice: z.number(),
  freeTrialDays: z.union([z.number(), z.string()]).nullish(),
});

const BillingExecutionCatalogProductV1Schema =
  BillingCatalogProductV1Schema.extend({
    effectivePrice: z.number(),
  });

// Mirror of CachedSubscriptionDiscount (schemas/stripe.ts) on the policy fact
// bundle. One entry per active Stripe discount, tagged bundle vs operator, with
// the discount id used to preserve an Operator Coupon without resetting its
// countdown. See ADR-0009.
const BillingSubscriptionDiscountV1Schema = z.object({
  discountId: z.string().min(1),
  couponId: z.string().optional(),
  percentOff: z.number().optional(),
  amountOff: z.number().optional(),
  name: z.string().optional(),
  end: z.number().optional(),
  source: z.enum(["bundle", "operator"]),
});

export type BillingSubscriptionDiscountV1 = z.infer<
  typeof BillingSubscriptionDiscountV1Schema
>;

const BillingCachedStripeSubscriptionSummaryV1Schema = z.object({
  stripeSubscriptionId: z.string().min(1),
  status: z.string().min(1),
  billingProvider: z
    .enum(SubscriptionBillingProviderList)
    .optional()
    .default("stripe"),
  // Whether a Shopify bridge was ever approved, which tells a renewal awaiting
  // settlement from a trial that ended unpaid. Absent on bundles serialized
  // before the field existed.
  billingEverApproved: z.boolean().nullish(),
  interval: z.enum(StripeIntervalList),
  intervalCount: z.number().int().positive(),
  periodEnd: z.number(),
  // Stripe's billing_cycle_anchor, absent on cache rows written before it.
  billingCycleAnchor: z.number().optional(),
  cancelAt: z.number().nullable(),
  trialEnd: z.number().nullable(),
  stripePriceId: z.string().min(1),
  version: z.string().nullish(),
  // Deprecated single-coupon alias (largest discount). New code reads `discounts`.
  coupon: z
    .object({
      id: z.string().optional(),
      percentOff: z.number().optional(),
      amountOff: z.number().optional(),
    })
    .optional(),
  // Full ordered Discount Stack from the cached subscription snapshot.
  discounts: z.array(BillingSubscriptionDiscountV1Schema).optional(),
});

const BillingSubscriptionItemV1Schema = z.object({
  subscriptionItemId: z.string().min(1),
  priceId: z.string().min(1),
  // The item's billed volume-tier quantity. Items added to this
  // subscription inherit it so every sibling bills the same tier;
  // null/absent when the cache has no live item (schedule fallback).
  quantity: z.number().int().nonnegative().nullish(),
  // Epoch-second end of an active per-module Trial Offer trial on this
  // item (`current_trial.end_date`); null/absent when not trialing.
  trialEndDate: z.number().nullish(),
});

const BillingActiveSubscriptionSnapshotV1Schema = z.object({
  billingProvider: z.enum(SubscriptionBillingProviderList).nullable(),
  stripeSubscriptionLinkId: z.string().uuid().nullable(),
  // The single Stripe sub these facts were narrowed to — the sub any provider
  // mutation must execute against. Absent/null on legacy bundles built before
  // single-target narrowing and when the org has no mutable live sub.
  stripeSubscriptionId: z.string().min(1).nullish(),
  // The live Shopify bridge selected from the requested/current subscription.
  // Distinct from stripeSubscriptionId: bridge subs are Shopify-billed and are
  // never directly mutable Stripe targets.
  shopifyBridgeStripeSubscriptionId: z.string().min(1).nullish(),
  currentCadence: z.enum(BillingCadenceList).nullable(),
  activeProducts: z.array(BillingCatalogProductV1Schema),
  cachedStripeSubscriptions: z.array(
    BillingCachedStripeSubscriptionSummaryV1Schema
  ),
  currentSubscriptionItems: z.array(BillingSubscriptionItemV1Schema).optional(),
  // The items of the selected Shopify bridge. The bridge is never the mutable
  // Stripe target, so they never appear in currentSubscriptionItems; on the
  // Shopify lane they are the owned price set.
  shopifyBridgeSubscriptionItems: z
    .array(BillingSubscriptionItemV1Schema)
    .optional(),
});

const BillingActiveOrderTierSnapshotV1Schema = z.object({
  id: z.string().uuid(),
  measuredAt: z.number(),
  metricName: z.string().min(1),
  metricSource: z.string().min(1),
  metricValue: z.number(),
  stripeTier: z.string().min(1),
  lockedUntil: z.number().nullable().optional(),
  lockReason: z.string().nullable().optional(),
  reason: z.object({
    summary: z.string().min(1),
    reasonCode: z.string().optional(),
    details: z.record(z.string(), z.unknown()).optional(),
  }),
});

export const BillingRequestedProductSetTargetV1Schema = z.object({
  source: z.enum([
    "requested_target",
    "active_subscription",
    "followup_payload",
    "none",
  ]),
  requestedProductSetTarget: BillingResolvedProductSetTargetSchema.optional(),
  requestedCadence: z.enum(BillingCadenceList).optional(),
  requestedEffectiveTiming: z.enum(BillingEffectiveTimingList).optional(),
  followupCommandType: z
    .enum(BillingScheduledFollowupCommandTypeList)
    .optional(),
  activePriceIds: z.array(z.string().min(1)).optional(),
});

// Which Stripe product owns a price the command payload references (one Stripe
// price backs exactly one product); null when the price is unknown to Stripe.
// Loaded with the fact bundle so policy resolution can validate (product, price)
// pairs without provider calls.
export const BillingPayloadPriceOwnershipV1Schema = z.object({
  priceId: z.string().min(1),
  stripeProductId: z.string().min(1).nullable(),
});

export type BillingPayloadPriceOwnershipV1 = z.infer<
  typeof BillingPayloadPriceOwnershipV1Schema
>;

export const BillingActiveShopifyAppSubscriptionV1Schema = z.object({
  shopifyAppSubscriptionId: z.string().min(1),
  shopifyAppSubscriptionLineItemId: z.string().min(1).nullable(),
  status: z.enum(["ACTIVE", "PENDING", "FROZEN"]),
  cadence: z.enum(BillingCadenceList).nullish(),
  owningBillingCommandId: z.string().min(1).nullable(),
  owningBillingCommandStatus: z.enum(BillingCommandStatusList).nullish(),
  // The Plan Intent the owning command was steering. An AppSubscription raised
  // by a Plan Intent is that intent's own work, not a rival subscription, so a
  // repair replaying the same intent must not read it as a duplicate of itself.
  owningPlanIntentId: z.string().min(1).nullish(),
  // The live usage line's capped amount and currency, when the matched line
  // item is usage-priced. Policy must compare only like currencies.
  cappedAmount: z.number().nullish(),
  cappedAmountCurrencyCode: z.string().length(3).nullish(),
  // What the live usage line has already drawn in the current Shopify period,
  // in the cap's currency. Shopify rejects a usage record once
  // balanceUsed + price exceeds cappedAmount, so a raise must be sized from
  // this plus what is still to be posted, not from the plan total alone.
  balanceUsed: z.number().nullish(),
});

export type BillingActiveShopifyAppSubscriptionV1 = z.infer<
  typeof BillingActiveShopifyAppSubscriptionV1Schema
>;

// Whether any org on the shop's myshopify domain ever held a subscription,
// soft-deleted ones and Bridge-Only Trials included: a returning shop gets no
// trial. Evaluators are pure, so this history arrives as a fact.
export const BillingTrialEligibilityFactsV1Schema = z.object({
  hasEverHadAnySubscription: z.boolean(),
});

export type BillingTrialEligibilityFactsV1 = z.infer<
  typeof BillingTrialEligibilityFactsV1Schema
>;

// A pause parked at its billing boundary (ADR-0037): a `completed` command
// whose boundary wakeup is still booked. `effectiveAt` is that boundary, not
// the earlier moment its wakeup fires, and `delayedJobId` is the job an undo
// or a superseding change releases.
export const BillingPendingPauseV1Schema = z.object({
  commandId: z.string().min(1),
  effectiveAt: z.string().min(1),
  delayedJobId: z.string().min(1),
});

export type BillingPendingPauseV1 = z.infer<typeof BillingPendingPauseV1Schema>;

// Why policy resolution refuses a pause. Each is a decisionScenarioId on the
// rejected decision.
export const BillingPauseRejectionReasonList = [
  "pause_boundary_unresolvable",
  "pause_conflicts_with_scheduled_cancel",
  "pause_not_allowed_during_trial",
  "pause_already_pending",
] as const;

export type BillingPauseRejectionReason =
  (typeof BillingPauseRejectionReasonList)[number];

// Why policy resolution parks a period-end wakeup for an operator: it ran after
// Stripe had already renewed the subscription, so the swap would land in a
// freshly invoiced period. A decisionScenarioId on the rejected decision.
export const PERIOD_END_BOUNDARY_MISSED = "period_end_boundary_missed";

// Why policy resolution parks a period-end wakeup that cannot check its
// boundary: the booked boundary or the live period end of the subscription it
// would change could not be read, so it cannot rule out a renewal.
export const PERIOD_END_BOUNDARY_UNVERIFIABLE =
  "period_end_boundary_unverifiable";

// Why policy resolution drops a scheduled_wakeup follow-up: a later command
// released the booking that fired it, after the wakeup had already minted it.
// A decisionScenarioId on the rejected decision.
export const SCHEDULED_WAKEUP_BOOKING_RELEASED =
  "scheduled_wakeup_booking_released";

export const BillingPolicyFactBundleV1Schema = z.object({
  schemaVersion: z.literal("v1"),
  commandId: z.string().min(1),
  orgId: z.string().min(1),
  normalizedIntent: BillingNormalizedIntentV1Schema,
  accountContext: BillingContextSchema,
  activeSubscriptionSnapshot:
    BillingActiveSubscriptionSnapshotV1Schema.nullish(),
  activeBillingOrderTierSnapshot:
    BillingActiveOrderTierSnapshotV1Schema.nullish(),
  activeShopifyAppSubscription:
    BillingActiveShopifyAppSubscriptionV1Schema.nullish(),
  // `not_requested` marks a Bridge-Only Trial, so a guard does not mistake it
  // for an AppSubscription it could not read. Absent without a live bridge, or
  // when the link or execution predates the field.
  shopifyBridgeProviderApproval: z.enum(ShopifyProviderApprovalList).nullish(),
  // The open Stripe invoice total on the org's Shopify bridge subscription:
  // what the charge poster will draw from the usage cap next. Absent when the
  // command is not Shopify-routed or the bridge owes nothing.
  shopifyBridgeOpenReceivable: ShopifyMoneyV1Schema.nullish(),
  availableCatalogProducts: z.array(BillingCatalogProductV1Schema),
  // Optional because in-flight executions serialized before ownership facts
  // existed carry no value (the state asserts validate without re-parsing, so
  // a schema default would never materialize) — policy resolution skips
  // ownership validation it has no facts for.
  payloadPriceOwnership: z
    .array(BillingPayloadPriceOwnershipV1Schema)
    .optional(),
  requestedTarget: BillingRequestedProductSetTargetV1Schema,
  // Resolved once before catalog build (operator-authored, else the sub's
  // billed quantity, else the metric) so price, cap and item quantity bill one
  // tier. Zero is real order volume; absent means unavailable, never zero.
  resolvedShopifyLaneQuantity: z.number().int().nonnegative().optional(),
  // Price ids actively trialing on the Shopify bridge subscription. A
  // product_set_change target must retain them: the in-place item replace
  // would delete a dropped item and destroy its Trial Offer mid-trial.
  // Absent when no bridge trial retention is needed or nothing is trialing.
  shopifyBridgeTrialingPriceIds: z.array(z.string().min(1)).optional(),
  // Price ids a scheduled_wakeup resume is deliberately revoking a trial on
  // (ADR 0013's explicit moduleTrialRevoke marker, the revoke counterpart to
  // its grant marker). Excluded from the shopifyBridgeTrialingPriceIds
  // retention check above: a commanded revocation is not the accidental drop
  // that check exists to catch. Absent when the command carries no revoke
  // marker.
  moduleTrialRevokedPriceIds: z.array(z.string().min(1)).optional(),
  planIntentSnapshot: BillingPlanIntentSnapshotV1Schema.optional(),
  // Optional for the same reason as payloadPriceOwnership: executions already
  // in flight when this shipped carry no value, so the eligibility gate passes
  // rather than rejecting on facts it never received.
  trialEligibilityFacts: BillingTrialEligibilityFactsV1Schema.optional(),
  // The pause parked on the fact bundle's subscription link, or null when none
  // is. Optional for executions already in flight when this shipped.
  pendingPause: BillingPendingPauseV1Schema.nullish(),
  // A period-end wakeup's booked boundary and the live Stripe period end of the
  // subscription its swap targets, both epoch seconds. A live period end past
  // the boundary means Stripe renewed first (PERIOD_END_BOUNDARY_MISSED); a
  // period-end wakeup missing either is PERIOD_END_BOUNDARY_UNVERIFIABLE.
  // Absent for every other command.
  bookedBoundary: z.number().optional(),
  liveTargetPeriodEnd: z.number().optional(),
  // A period-end wakeup whose source plan names no period-end boundary: a
  // Shopify scheduled cancel whose bridge could not be narrowed to one period
  // end, or one booked exactly on its boundary with no lead. There is no renewal
  // to verify it against, so the boundary guard lets it through. Absent for every
  // other command.
  wakeupBookedOffPeriodEnd: z.literal(true).optional(),
  // A scheduled_wakeup follow-up whose booking its source command's
  // releasedWakeupBookings holds (SCHEDULED_WAKEUP_BOOKING_RELEASED). Absent
  // for every other command.
  wakeupBookingReleased: z.literal(true).optional(),
});

export const BillingPolicyDecisionSetV1Schema = z.discriminatedUnion(
  "policyStatus",
  [
    z.object({
      schemaVersion: z.literal("v1"),
      commandId: z.string().min(1),
      orgId: z.string().min(1),
      billingProvider: z.enum(SubscriptionBillingProviderList),
      policyStatus: z.literal("accepted"),
      originScenarioId: z.string().min(1),
      decisionScenarioId: z.string().min(1),
      executionMode: z.enum([
        "immediate_provider_mutation",
        "no_provider_mutation",
      ]),
      resolvedProductSetTarget:
        BillingResolvedProductSetTargetSchema.optional(),
      resolvedCadence: z.enum(BillingCadenceList).optional(),
      requestedEffectiveTiming: z.enum(BillingEffectiveTimingList).optional(),
      appliedPolicies: z.array(z.string().min(1)).optional(),
      explanation: z
        .object({
          summary: z.string().min(1),
          details: z.record(z.string(), z.unknown()).optional(),
        })
        .optional(),
      workflowCheckpointJson: BillingRuntimeCheckpointV1Schema,
    }),
    z.object({
      schemaVersion: z.literal("v1"),
      commandId: z.string().min(1),
      orgId: z.string().min(1),
      billingProvider: z.enum(SubscriptionBillingProviderList),
      policyStatus: z.literal("deferred"),
      originScenarioId: z.string().min(1),
      decisionScenarioId: z.string().min(1),
      executionMode: z.literal("deferred_schedule_only"),
      deferredBoundaryReason: z.enum(BillingScheduledBoundaryReasonList),
      resolvedProductSetTarget:
        BillingResolvedProductSetTargetSchema.optional(),
      resolvedCadence: z.enum(BillingCadenceList).optional(),
      requestedEffectiveTiming: z.enum(BillingEffectiveTimingList).optional(),
      appliedPolicies: z.array(z.string().min(1)).optional(),
      explanation: z
        .object({
          summary: z.string().min(1),
          details: z.record(z.string(), z.unknown()).optional(),
        })
        .optional(),
      workflowCheckpointJson: BillingRuntimeCheckpointV1Schema,
    }),
    z.object({
      schemaVersion: z.literal("v1"),
      commandId: z.string().min(1),
      orgId: z.string().min(1),
      billingProvider: z.enum(SubscriptionBillingProviderList),
      policyStatus: z.literal("rejected"),
      originScenarioId: z.string().min(1),
      decisionScenarioId: z.string().min(1),
      executionMode: z.literal("no_provider_mutation"),
      reason: z.string().min(1),
      appliedPolicies: z.array(z.string().min(1)).optional(),
      explanation: z
        .object({
          summary: z.string().min(1),
          details: z.record(z.string(), z.unknown()).optional(),
        })
        .optional(),
      workflowCheckpointJson: BillingRuntimeCheckpointV1Schema,
    }),
    z.object({
      schemaVersion: z.literal("v1"),
      commandId: z.string().min(1),
      orgId: z.string().min(1),
      billingProvider: z.enum(SubscriptionBillingProviderList),
      policyStatus: z.literal("blocked"),
      originScenarioId: z.string().min(1),
      decisionScenarioId: z.string().min(1),
      executionMode: z.literal("no_provider_mutation"),
      reason: z.string().min(1),
      explanation: z.object({
        summary: z.string().min(1),
        details: z.record(z.string(), z.unknown()).optional(),
      }),
      workflowCheckpointJson: BillingRuntimeCheckpointV1Schema,
    }),
  ]
);

const BillingCommercialInputsV1Schema = z.object({
  subtotal: z.number(),
  effectiveTotal: z.number(),
  discountInstructions: z.array(
    z
      .object({
        kind: z.string().min(1),
      })
      .catchall(z.unknown())
  ),
  products: z.array(BillingExecutionCatalogProductV1Schema),
});

const BillingEntitlementInputsV1Schema = z.object({
  productIds: z.array(z.string().min(1)),
});

export const BillingExecutionPlanEnvelopeV1Schema = z.discriminatedUnion(
  "planType",
  [
    z
      .object({
        schemaVersion: z.literal("v1"),
        commandId: z.string().min(1),
        orgId: z.string().min(1),
        billingProvider: z.enum(SubscriptionBillingProviderList),
        planType: z.literal("immediate_provider_mutation"),
        originScenarioId: z.string().min(1).optional(),
        decisionScenarioId: z.string().min(1).optional(),
        executionPlanVersion: z.string().min(1),
        idempotencyKeys: IdempotencyKeysSchema,
        commercialInputs: BillingCommercialInputsV1Schema,
        entitlementInputs: BillingEntitlementInputsV1Schema,
        providerInputs: StripeProviderExecutionInputsSchema.optional(),
        providerMutation: StripeProviderMutationV1Schema.nullish(),
        existingSubscriptionRefs: ExistingSubscriptionRefsSchema,
        existingShopifyRefs: ShopifyExistingRefsSchema.optional(),
        planIntentRef: BillingPlanIntentRefV1Schema.optional(),
        shopifyExecutionMode: ShopifyExecutionModeSchema.optional(),
        // A pending pause this change supersedes (ADR-0037), released once the
        // provider mutation succeeds.
        releaseScheduledWakeup:
          BillingReleaseScheduledWakeupV1Schema.optional(),
        workflowCheckpointJson: BillingRuntimeCheckpointV1Schema,
        releaseScheduledWakeups: z
          .array(
            z.object({
              sourceCommandId: z.string().min(1),
              delayedJobId: z.string().min(1),
            })
          )
          .optional(),
      })
      .superRefine((value, ctx) => {
        if (
          value.shopifyExecutionMode !== undefined &&
          value.billingProvider !== "shopify"
        ) {
          ctx.addIssue({
            code: "custom",
            path: ["shopifyExecutionMode"],
            message:
              "shopifyExecutionMode is only valid for Shopify immediate plans",
          });
        }
      }),
    z.object({
      schemaVersion: z.literal("v1"),
      commandId: z.string().min(1),
      orgId: z.string().min(1),
      billingProvider: z.enum(SubscriptionBillingProviderList),
      planType: z.literal("record_only"),
      originScenarioId: z.string().min(1).optional(),
      decisionScenarioId: z.string().min(1).optional(),
      planIntentRef: BillingPlanIntentRefV1Schema,
      entitlementInputs: z.never().optional(),
      workflowCheckpointJson: BillingRuntimeCheckpointV1Schema,
    }),
    z.object({
      schemaVersion: z.literal("v1"),
      commandId: z.string().min(1),
      orgId: z.string().min(1),
      billingProvider: z.enum(SubscriptionBillingProviderList),
      planType: z.literal("deferred_schedule_only"),
      originScenarioId: z.string().min(1).optional(),
      decisionScenarioId: z.string().min(1).optional(),
      // When the change takes effect; `dueAt` is when its wakeup fires, an
      // hour earlier for a period-end boundary.
      boundary: z.string().min(1).optional(),
      dueAt: z.string().min(1),
      deferredBoundaryReason: z.enum(BillingScheduledBoundaryReasonList),
      followupCommandType: z.enum(BillingScheduledFollowupCommandTypeList),
      executionPlanVersion: z.string().min(1),
      idempotencyKey: z.string().min(1),
      followupPayload: BillingFollowupPayloadSchema.optional(),
      // A pending pause this deferred change supersedes (ADR-0037), released
      // when the change books its own boundary.
      releaseScheduledWakeup: BillingReleaseScheduledWakeupV1Schema.optional(),
      workflowCheckpointJson: BillingRuntimeCheckpointV1Schema,
    }),
    z.object({
      schemaVersion: z.literal("v1"),
      commandId: z.string().min(1),
      orgId: z.string().min(1),
      billingProvider: z.enum(SubscriptionBillingProviderList),
      planType: z.literal("no_provider_mutation"),
      originScenarioId: z.string().min(1).optional(),
      decisionScenarioId: z.string().min(1).optional(),
      planIntentRef: BillingPlanIntentRefV1Schema.optional(),
      internalOutcome: z.string().min(1),
      agencyLifecycle: AgencyLifecyclePayloadSchema.optional(),
      providerLifecycleAction: ProviderLifecycleActionSchema.optional(),
      // A two-phase internal plan: the lifecycle action applies now and this
      // block schedules the boundary follow-up (e.g. a Shopify schedule_cancel
      // sets the bridge sub's cancelAt immediately and defers the irreversible
      // AppSubscription cancel to a scheduled_cancel wakeup).
      scheduledWakeup: BillingScheduledWakeupV1Schema.optional(),
      releaseScheduledWakeup: BillingReleaseScheduledWakeupV1Schema.optional(),
      // Releases confirmed module-trial-cancellation wakeups (see the
      // immediate_provider_mutation variant above) — a no_provider_mutation
      // plan reaches the same downstream-effects step and needs the same
      // field, since a trial-only confirm resolves to no Stripe mutation.
      releaseScheduledWakeups: z
        .array(
          z.object({
            sourceCommandId: z.string().min(1),
            delayedJobId: z.string().min(1),
          })
        )
        .optional(),
      commercialInputs: BillingCommercialInputsV1Schema.optional(),
      // The org's whole projected entitlement set: the projection step sets
      // scopes to exactly these product ids, so an empty list is a deliberate
      // revoke. Present only when the plan's outcome defines that set; a plan
      // that leaves the merchant's access as it found it (a release, a
      // schedule_cancel before its boundary, an agency lifecycle action whose
      // scopes come from product links) omits the field so the projection step
      // touches nothing.
      entitlementInputs: BillingEntitlementInputsV1Schema.optional(),
      workflowCheckpointJson: BillingRuntimeCheckpointV1Schema,
    }),
  ]
);

export const BillingPolicyFactLoadingStateV1Schema = z.object({
  runtimeContract: z.string().min(1),
  orgId: z.string().min(1),
  commandId: z.string().min(1),
  commandFamily: z.string().min(1),
  intent: z.object({ Payload: BillingNormalizedIntentV1Schema }),
});

export const BillingPolicyApplicationStateV1Schema = z.object({
  runtimeContract: z.string().min(1),
  orgId: z.string().min(1),
  commandId: z.string().min(1),
  commandFamily: z.string().min(1),
  intent: z.object({ Payload: BillingNormalizedIntentV1Schema }),
  policyFacts: z.object({ Payload: BillingPolicyFactBundleV1Schema }),
});

export const BillingPolicyPlanAssemblyStateV1Schema = z.object({
  runtimeContract: z.string().min(1),
  orgId: z.string().min(1),
  commandId: z.string().min(1),
  commandFamily: z.string().min(1),
  intent: z.object({ Payload: BillingNormalizedIntentV1Schema }),
  policyFacts: z.object({ Payload: BillingPolicyFactBundleV1Schema }),
  policyDecision: z.object({ Payload: BillingPolicyDecisionSetV1Schema }),
});

export const BillingInternalSchedulingStateV1Schema =
  BillingCommandRuntimeEnvelopeV1Schema.extend({
    executionPlan: z.object({ Payload: BillingExecutionPlanEnvelopeV1Schema }),
  });

export type BillingRequestedProductSetTargetV1 = z.infer<
  typeof BillingRequestedProductSetTargetV1Schema
>;

export type BillingPolicyFactBundleV1 = z.infer<
  typeof BillingPolicyFactBundleV1Schema
>;

export type BillingPolicyDecisionSetV1 = z.infer<
  typeof BillingPolicyDecisionSetV1Schema
>;

export type BillingExecutionPlanEnvelopeV1 = z.infer<
  typeof BillingExecutionPlanEnvelopeV1Schema
>;

export type BillingCatalogProductV1 = z.infer<
  typeof BillingCatalogProductV1Schema
>;

export type BillingActiveSubscriptionSnapshotV1 = z.infer<
  typeof BillingActiveSubscriptionSnapshotV1Schema
>;

export type BillingActiveOrderTierSnapshotV1 = z.infer<
  typeof BillingActiveOrderTierSnapshotV1Schema
>;

export type BillingPolicyFactLoadingStateV1 = z.infer<
  typeof BillingPolicyFactLoadingStateV1Schema
>;

export type BillingPolicyApplicationStateV1 = z.infer<
  typeof BillingPolicyApplicationStateV1Schema
>;

export type BillingPolicyPlanAssemblyStateV1 = z.infer<
  typeof BillingPolicyPlanAssemblyStateV1Schema
>;

export type BillingInternalSchedulingStateV1 = z.infer<
  typeof BillingInternalSchedulingStateV1Schema
>;

export function assertBillingPolicyFactBundle(
  value: unknown
): asserts value is BillingPolicyFactBundleV1 {
  const parsed = BillingPolicyFactBundleV1Schema.safeParse(value);
  if (!parsed.success) {
    throw billingStateAssertionError(
      "billing policy fact bundle is missing required fields",
      parsed.error
    );
  }
}

export function assertBillingPolicyDecisionSet(
  value: unknown
): asserts value is BillingPolicyDecisionSetV1 {
  const parsed = BillingPolicyDecisionSetV1Schema.safeParse(value);
  if (!parsed.success) {
    throw billingStateAssertionError(
      "billing policy decision set is missing required fields",
      parsed.error
    );
  }
}

export function assertBillingExecutionPlanEnvelope(
  value: unknown
): asserts value is BillingExecutionPlanEnvelopeV1 {
  const parsed = BillingExecutionPlanEnvelopeV1Schema.safeParse(value);
  if (!parsed.success) {
    throw billingStateAssertionError(
      "billing execution plan envelope is missing required fields",
      parsed.error
    );
  }
}

export function assertBillingPolicyFactLoadingState(
  value: unknown
): asserts value is BillingPolicyFactLoadingStateV1 {
  const parsed = BillingPolicyFactLoadingStateV1Schema.safeParse(value);
  if (!parsed.success) {
    throw billingStateAssertionError(
      "billing policy fact-loading state is missing intent payload",
      parsed.error
    );
  }
}

export function assertBillingPolicyApplicationState(
  value: unknown
): asserts value is BillingPolicyApplicationStateV1 {
  const parsed = BillingPolicyApplicationStateV1Schema.safeParse(value);
  if (!parsed.success) {
    throw billingStateAssertionError(
      "billing policy-application state is missing fact payload",
      parsed.error
    );
  }
}

export function assertBillingPolicyPlanAssemblyState(
  value: unknown
): asserts value is BillingPolicyPlanAssemblyStateV1 {
  const parsed = BillingPolicyPlanAssemblyStateV1Schema.safeParse(value);
  if (!parsed.success) {
    throw billingStateAssertionError(
      "billing policy plan-assembly state is missing decision payload",
      parsed.error
    );
  }
}

export function assertBillingInternalSchedulingState(
  value: unknown
): asserts value is BillingInternalSchedulingStateV1 {
  const parsed = BillingInternalSchedulingStateV1Schema.safeParse(value);
  if (!parsed.success) {
    throw billingStateAssertionError(
      "billing internal-scheduling state is missing execution-plan payload",
      parsed.error
    );
  }
}

export function toBillingCadenceFromStripeInterval(
  interval: StripeInterval,
  intervalCount?: number
): BillingCadence | null {
  if (interval === "year") {
    return "annual";
  }

  if (interval === "month") {
    if (intervalCount !== 3 && intervalCount !== 6) {
      return "monthly";
    }

    return intervalCount === 6 ? "six_month" : "quarterly";
  }

  return null;
}
